import { useCallback, useState, useEffect, useMemo, useRef } from 'preact/hooks'

import config from '../config'
import withStyles from '../components/jss'
import BrainrotProblem from '../components/brainrot-problem'
import Problem from '../components/problem'
import NotStarted from '../components/not-started'
import { useToast } from '../components/toast'
import Chevron from '../icons/chevron-down-solid.svg'
import Volume from '../icons/volume.svg'
import VolumeMute from '../icons/volume-mute.svg'

import {
  getAdminChallenges,
  getChallenges,
  getPrivateSolves,
} from '../api/challenges'

const loadStates = {
  pending: 0,
  notStarted: 1,
  loaded: 2,
}

const Challenges = ({ classes, admin }) => {
  const challPageState = useMemo(
    () => JSON.parse(localStorage.getItem('challPageState') || '{}'),
    []
  )
  const [problems, setProblems] = useState(null)
  const [categories, setCategories] = useState(challPageState.categories || {})
  const [showSolved, setShowSolved] = useState(
    challPageState.showSolved === undefined ? true : challPageState.showSolved
  )
  const [solveIDs, setSolveIDs] = useState([])
  const [loadState, setLoadState] = useState(loadStates.pending)
  const { toast } = useToast()

  const [brainrotUI, setBrainrotUI] = useState(
    challPageState.brainrotUI === undefined ? true : challPageState.brainrotUI
  )

  const setSolved = useCallback(id => {
    setSolveIDs(solveIDs => {
      if (!solveIDs.includes(id)) {
        return [...solveIDs, id]
      }
      return solveIDs
    })
  }, [])

  const handleShowSolvedChange = useCallback(e => {
    setShowSolved(e.target.checked)
  }, [])

  const handleBrainrotUIChange = useCallback(
    e => setBrainrotUI(e.target.checked),
    []
  )

  const handleCategoryCheckedChange = useCallback(e => {
    setCategories(categories => ({
      ...categories,
      [e.target.dataset.category]: e.target.checked,
    }))
  }, [])

  const listRef = useRef(null)

  const categoryDropdownRef = useRef(null)

  const toggleCategoryDropdown = useCallback(e => {
    const ref = categoryDropdownRef.current

    if (ref === undefined) {
      return
    }

    if (ref.open) {
      ref.close()
    } else {
      ref.show()
    }
  }, [])

  useEffect(() => {
    document.title = `Challenges | ${config.ctfName}`
  }, [])

  const [challengeVideos, setChallengeVideos] = useState(null)
  useEffect(() => {
    const action = async () => {
      const res = await fetch('https://static.umdctf.io/videos.json')
      const json = await res.json()
      setChallengeVideos(json)
    }
    action()
  }, [])

  useEffect(() => {
    const action = async () => {
      if (problems !== null) {
        return
      }
      let challs
      if (admin) {
        const { data, error } = await getAdminChallenges()
        if (error) {
          toast({ body: error, type: 'error' })
          return
        }

        setLoadState(loadStates.loaded)

        challs = data.map(chall => ({ ...chall, points: 727 }))
      } else {
        const { data, error, notStarted } = await getChallenges()
        if (error) {
          toast({ body: error, type: 'error' })
          return
        }

        setLoadState(notStarted ? loadStates.notStarted : loadStates.loaded)
        if (notStarted) {
          return
        }

        challs = data
      }

      const newCategories = { ...categories }
      challs.forEach(problem => {
        if (newCategories[problem.category] === undefined) {
          newCategories[problem.category] = false
        }
      })

      setProblems(challs)
      setCategories(newCategories)
    }
    action()
  }, [toast, categories, problems, admin])

  useEffect(() => {
    const action = async () => {
      const { data, error } = await getPrivateSolves()
      if (error) {
        toast({ body: error, type: 'error' })
        return
      }

      setSolveIDs(data.map(solve => solve.id))
    }
    action()
  }, [toast])

  useEffect(() => {
    localStorage.challPageState = JSON.stringify({
      categories,
      showSolved,
      brainrotUI,
    })
  }, [categories, showSolved, brainrotUI])

  const problemsToDisplay = useMemo(() => {
    if (problems === null) {
      return []
    }
    let filtered = problems
    if (!showSolved) {
      filtered = filtered.filter(problem => !solveIDs.includes(problem.id))
    }
    let filterCategories = false
    Object.values(categories).forEach(displayCategory => {
      if (displayCategory) filterCategories = true
    })
    if (filterCategories) {
      Object.keys(categories).forEach(category => {
        if (categories[category] === false) {
          // Do not display this category
          filtered = filtered.filter(problem => problem.category !== category)
        }
      })
    }

    filtered.sort((a, b) => {
      if (a.points === b.points) {
        if (a.solves === b.solves) {
          const aWeight = a.sortWeight || 0
          const bWeight = b.sortWeight || 0

          return bWeight - aWeight
        }
        return b.solves - a.solves
      }
      return a.points - b.points
    })

    return filtered
  }, [problems, categories, showSolved, solveIDs])

  const { categoryCounts, solvedCount } = useMemo(() => {
    const categoryCounts = new Map()
    let solvedCount = 0
    if (problems !== null) {
      for (const problem of problems) {
        if (!categoryCounts.has(problem.category)) {
          categoryCounts.set(problem.category, {
            total: 0,
            solved: 0,
          })
        }

        const solved = solveIDs.includes(problem.id)
        categoryCounts.get(problem.category).total += 1
        if (solved) {
          categoryCounts.get(problem.category).solved += 1
        }

        if (solved) {
          solvedCount += 1
        }
      }
    }
    return { categoryCounts, solvedCount }
  }, [problems, solveIDs])

  const displayCategory = useMemo(() => {
    let num = 0
    Object.values(categories).forEach(displayCategory => {
      if (displayCategory) num += 1
    })
    if (num === 1) {
      return Object.keys(categories).filter(category => {
        return categories[category]
      })[0]
    }
    if (num === 0 || num === categoryCounts.size) {
      return 'all categories'
    }
    return `${num} categories`
  }, [categories, categoryCounts])

  const curVideoRef = useRef(null)

  useEffect(() => {
    const container = listRef.current
    if (container === null) return
    const onIntersectionObserved = entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
          const vid = entry.target.querySelector('video')
          if (curVideoRef.current) {
            curVideoRef.current.pause()
            curVideoRef.current.currentTime = 0
          }
          curVideoRef.current = vid
          if (vid !== null) {
            vid.play()
          }
        }
      })
    }
    const observer = new IntersectionObserver(onIntersectionObserved, {
      root: container,
      threshold: 0.6,
    })
    Array.from(container.children).forEach(e => observer.observe(e))
  })

  const [muted, setMuted] = useState(true)

  function toggleMute() {
    setMuted(!muted)
  }

  if (loadState === loadStates.pending) {
    return null
  }

  if (loadState === loadStates.notStarted) {
    return <NotStarted />
  }

  return (
    <>
      {brainrotUI ? (
        <div className={classes.centering}>
          <div className={classes.categoryAnchor}>
            <div className={classes.categoryContainer}>
              <div
                className={classes.categorySelect}
                onClick={toggleCategoryDropdown}
              >
                {displayCategory}
                <Chevron />
              </div>
              <div className={classes.muteButton} onClick={toggleMute}>
                {muted ? <VolumeMute /> : <Volume />}
              </div>
              <div style='flex: 100% 0 0;'>&nbsp;</div>
              <dialog
                className={classes.categoryDropdown}
                ref={categoryDropdownRef}
              >
                {Array.from(categoryCounts.entries())
                  .sort((a, b) => a[0].localeCompare(b[0]))
                  .map(([category, { solved, total }]) => {
                    return (
                      <div
                        key={category}
                        className='form-ext-control form-ext-checkbox'
                      >
                        <input
                          className='form-ext-input brainrotCheck'
                          id={`category-${category}`}
                          data-category={category}
                          type='checkbox'
                          checked={categories[category]}
                          onChange={handleCategoryCheckedChange}
                        />
                        <label
                          htmlFor={`category-${category}`}
                          className='form-ext-label'
                        >
                          {category} ({solved}/{total} solved)
                        </label>
                      </div>
                    )
                  })}
                <hr style={{ width: '70%', margin: '.5rem auto 0 auto' }} />
                <div className='form-ext-control form-ext-checkbox'>
                  <input
                    className='form-ext-input brainrotCheck'
                    id='show-solved'
                    type='checkbox'
                    checked={showSolved}
                    onChange={handleShowSolvedChange}
                  />
                  <label htmlFor='show-solved' className='form-ext-label'>
                    Show Solved ({solvedCount}/{problems.length} solved)
                  </label>
                </div>
                <hr style={{ width: '70%', margin: '.5rem auto 0 auto' }} />
                <div className='form-ext-control form-ext-checkbox'>
                  <input
                    className='form-ext-input brainrotCheck'
                    id='view-brainrot'
                    type='checkbox'
                    checked
                    onChange={handleBrainrotUIChange}
                  />
                  <label htmlFor='view-brainrot' className='form-ext-label'>
                    Use Brainrot UI
                  </label>
                </div>
              </dialog>
            </div>
            <div className={classes.setWidth}>
              <div className={classes.challList} ref={listRef}>
                {problemsToDisplay.map(problem => {
                  return (
                    <BrainrotProblem
                      key={problem.id}
                      problem={problem}
                      solved={solveIDs.includes(problem.id)}
                      setSolved={setSolved}
                      challengeVideos={challengeVideos}
                      admin={admin}
                      muted={muted}
                    />
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className={`row ${classes.row}`}>
          <div className='col-3'>
            <div className={`frame ${classes.frame}`}>
              <div className='frame__body'>
                <div className='frame__title title'>View</div>
                <div className='form-ext-control form-ext-checkbox'>
                  <input
                    id='view-brainrot'
                    className='form-ext-input'
                    type='checkbox'
                    checked={brainrotUI}
                    onChange={handleBrainrotUIChange}
                  />
                  <label htmlFor='view-brainrot' className='form-ext-label'>
                    Use Brainrot UI
                  </label>
                </div>
              </div>
            </div>
            <div className={`frame ${classes.frame}`}>
              <div className='frame__body'>
                <div className='frame__title title'>Filters</div>
                <div className={classes.showSolved}>
                  <div className='form-ext-control form-ext-checkbox'>
                    <input
                      id='show-solved'
                      className='form-ext-input'
                      type='checkbox'
                      checked={showSolved}
                      onChange={handleShowSolvedChange}
                    />
                    <label htmlFor='show-solved' className='form-ext-label'>
                      Show Solved ({solvedCount}/{problems.length} solved)
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div className={`frame ${classes.frame}`}>
              <div className='frame__body'>
                <div className='frame__title title'>Categories</div>
                {Array.from(categoryCounts.entries())
                  .sort((a, b) => a[0].localeCompare(b[0]))
                  .map(([category, { solved, total }]) => {
                    return (
                      <div
                        key={category}
                        className='form-ext-control form-ext-checkbox'
                      >
                        <input
                          id={`category-${category}`}
                          data-category={category}
                          className='form-ext-input'
                          type='checkbox'
                          checked={categories[category]}
                          onChange={handleCategoryCheckedChange}
                        />
                        <label
                          htmlFor={`category-${category}`}
                          className='form-ext-label'
                        >
                          {category} ({solved}/{total} solved)
                        </label>
                      </div>
                    )
                  })}
              </div>
            </div>
          </div>
          <div className='col-6'>
            {problemsToDisplay.map(problem => {
              return (
                <Problem
                  key={problem.id}
                  problem={problem}
                  solved={solveIDs.includes(problem.id)}
                  setSolved={setSolved}
                />
              )
            })}
          </div>
        </div>
      )}
    </>
  )
}

export default withStyles(
  {
    centering: {
      height: '100%',
      maxHeight: '100vh',
      width: '100%',
      maxWidth: '100%',
      display: 'flex',
      justifyContent: 'center',
      overflow: 'hidden',
    },
    categoryAnchor: {
      height: '100%',
      position: 'relative',
    },
    categoryContainer: {
      display: 'flex',
      flexFlow: 'row wrap',
      justifyContent: 'space-between',
      width: 'calc(100% - 2rem)',
      position: 'absolute',
      top: '1.25rem',
      left: '.5rem',
      zIndex: 2,
    },
    setWidth: {
      height: '100%',
      maxWidth: '100cqw',
      aspectRatio: '9/16',
      display: 'flex',
      flexDirection: 'column',
    },
    challList: {
      height: '100%',
      scrollSnapType: 'y mandatory',
      overflowX: 'hidden',
      overflowY: 'scroll',
      position: 'relative',
      scrollbarWidth: 'thin',
    },
    categorySelect: {
      fontWeight: 'bold',
      cursor: 'pointer',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      backgroundImage:
        'radial-gradient(#00000090, #00000040, #00000000, #00000000)',
      padding: '.5rem 1rem',
      '& svg': {
        marginLeft: '.5rem',
        height: '1.25rem',
      },
    },
    categoryDropdown: {
      border: '0px solid black',
      borderRadius: '0.5rem',
      padding: '0.25rem',
      paddingTop: '0',
      marginTop: '-1.5rem',
      background: 'white',
      position: 'static',
      width: 'max-content',
    },
    muteButton: {
      cursor: 'pointer',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      padding: '0px 1rem',
      marginRight: '-1.25rem',
      backgroundImage:
        'radial-gradient(#00000090, #00000040, #00000000, #00000000)',
      '& svg': {
        height: '1.25rem',
      },
    },
    showSolved: {
      marginBottom: '0.625em',
    },
    frame: {
      marginBottom: '1em',
      paddingBottom: '0.625em',
      background: '#222',
    },
    row: {
      justifyContent: 'center',
      '& .title, & .frame__subtitle': {
        color: '#fff',
      },
    },
  },
  Challenges
)
